import { Component, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { FormsModule, ReactiveFormsModule, FormControl } from '@angular/forms';

// Angular Material & Others
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import {
    MatPaginatorModule,
    MatPaginator,
    PageEvent,
} from '@angular/material/paginator';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTableDataSource } from '@angular/material/table';
import { MatMenuModule } from '@angular/material/menu';
import { MatSelectModule } from '@angular/material/select';

import { ItinerarioService } from '../../services/itinerario.service';
import { ItinerariosStateService } from '../../services/itinerarios-state.service';
import { ItinerariosNavigationService } from '../../services/itinerarios-navigation.service';
import { InstructivoEmbarqueNavigationService } from '../instructivo-embarque/services/instructivo-embarque-navigation.service';
import { BookingsService } from '../bookings/services/bookings.service';
import { formatDateForApi } from '../../core/utils/date.utils';
import { ActivatedRoute } from '@angular/router';
import { ItinerarioFiltroDto, ItinerarioItemDto } from '@opb-workspace/shared';
import * as ExcelJS from 'exceljs';

@Component({
    selector: 'app-itinerarios',
    standalone: true,
    imports: [
        CommonModule,
        RouterModule,
        FormsModule,
        ReactiveFormsModule,
        MatCardModule,
        MatButtonModule,
        MatPaginatorModule,
        MatFormFieldModule,
        MatAutocompleteModule,
        MatInputModule,
        MatIconModule,
        MatTooltipModule,
        MatDatepickerModule,
        MatNativeDateModule,
        MatProgressSpinnerModule,
        MatMenuModule,
        MatSelectModule,
    ],
    templateUrl: './itinerarios.component.html',
    styleUrls: ['./itinerarios.component.scss'],
})
export class ItinerariosComponent implements OnInit {
    @ViewChild(MatPaginator) paginator!: MatPaginator;
    canViewInstructivoButton = false;

    // Data Source for managing data easily
    dataSource = new MatTableDataSource<ItinerarioItemDto>([]);

    // States
    isLoading = false;
    hasError = false;
    errorMessage = '';
    showRequiredFiltersMessage = true; // Show initial message

    // Pagination
    totalRegistros = 0;
    paginaActual = 1;
    elementosPorPagina = 10;
    weeksPeriod = 5; // Default period

    // Filters Controls
    filtroPol = new FormControl('');
    filtroPod = new FormControl('');
    filtroCarrier = new FormControl('');
    filtroNave = new FormControl('');
    filtroServicio = new FormControl<number | null>(null);

    // Date Ranges
    filtroEtdDesde = new FormControl<Date | null>(null);
    filtroEtdHasta = new FormControl<Date | null>(null);

    filtroEtaDesde = new FormControl<Date | null>(null);
    filtroEtaHasta = new FormControl<Date | null>(null);

    // Options for Autocompletes
    polPortsOptions: any[] = [];
    podPortsOptions: any[] = [];
    carriersOptions: any[] = [];
    vesselsOptions: any[] = [];

    filteredPolOptions: any[] = [];
    filteredPodOptions: any[] = [];
    filteredCarrierOptions: any[] = [];
    filteredVesselsOptions: any[] = [];

    // Service Type Options (1 = Liner, 2 = Charter)
    servicioOptions = [
        { value: null, label: 'Todos' },
        { value: 1, label: 'Liner' },
        { value: 2, label: 'Charter' },
    ];

    constructor(
        private itinerarioService: ItinerarioService,
        private stateService: ItinerariosStateService,
        private navigationService: ItinerariosNavigationService,
        private route: ActivatedRoute,
        private ieNavigationService: InstructivoEmbarqueNavigationService,
        private bookingsService: BookingsService
    ) {}

    ngOnInit(): void {
        // TEMP (2026-05): mostrar instructivo a todos los roles; revertir a hasRole('admin') cuando cierre el piloto.
        this.canViewInstructivoButton = true;
        this.loadCombos();
        this.setupFilterListeners();

        // 1. Try to load state from URL (query params)
        const stateLoadedFromUrl = this.navigationService.loadStateFromUrl(
            this.route
        );

        // 2. If no query params in URL, this is a fresh navigation (via sidebar)
        //    Clear any cached state to start fresh
        if (!stateLoadedFromUrl) {
            this.stateService.clearState();
            // Show initial required filters message, don't restore anything
            this.showRequiredFiltersMessage = true;
            return; // Exit early - no filters to restore
        }

        // 3. Only restore state if we loaded it from URL (returning from detail)
        const state = this.stateService.getCurrentState();
        if (state.filters) {
            const f = state.filters;
            // Patch Form Controls
            if (f.pol) this.filtroPol.setValue(f.pol);
            if (f.pod) this.filtroPod.setValue(f.pod);
            if (f.carrier) this.filtroCarrier.setValue(f.carrier);
            if (f.vessel) this.filtroNave.setValue(f.vessel);
            if (f.typeOfService) this.filtroServicio.setValue(f.typeOfService);

            if (f.startDate)
                this.filtroEtdDesde.setValue(new Date(f.startDate));
            if (f.endDate) this.filtroEtdHasta.setValue(new Date(f.endDate));

            if (f.dateEtaFrom)
                this.filtroEtaDesde.setValue(new Date(f.dateEtaFrom));
            if (f.dateEtaTo)
                this.filtroEtaHasta.setValue(new Date(f.dateEtaTo));

            // Restore pagination
            this.paginaActual = f.page || 1;
            this.elementosPorPagina = f.limit || 10;

            // Si había nave seleccionada, bloquear ETD Desde con la fecha guardada o hoy
            if (f.vessel) {
                if (!this.filtroEtdDesde.value) {
                    this.filtroEtdDesde.setValue(new Date());
                }
                this.filtroEtdDesde.disable();
            }
        }

        // 4. Search if returning from detail with previously applied filters
        if (this.hasRequiredFilters()) {
            this.showRequiredFiltersMessage = false;
            this.buscarItinerarios();
        }
    }

    loadCombos() {
        // Cargar puertos POL (PortTypeId = 1)
        this.itinerarioService.getPolPorts().subscribe((data) => {
            this.polPortsOptions = data;
            this.filteredPolOptions = data;
        });

        // Cargar puertos POD (PortTypeId = 2)
        this.itinerarioService.getPodPorts().subscribe((data) => {
            this.podPortsOptions = data;
            this.filteredPodOptions = data;
        });

        this.itinerarioService.getCarriers().subscribe((data) => {
            this.carriersOptions = data;
            this.filteredCarrierOptions = data;
        });

        this.bookingsService.getFilters().subscribe((data) => {
            this.vesselsOptions = data.vessels || [];
            this.filteredVesselsOptions = [...this.vesselsOptions];
        });
    }

    setupFilterListeners() {
        this.filtroPol.valueChanges.subscribe((val) => {
            this.filteredPolOptions = this._filter(
                this.polPortsOptions,
                val || ''
            );
        });

        this.filtroPod.valueChanges.subscribe((val) => {
            this.filteredPodOptions = this._filter(
                this.podPortsOptions,
                val || ''
            );
        });

        this.filtroCarrier.valueChanges.subscribe((val) => {
            this.filteredCarrierOptions = this._filter(
                this.carriersOptions,
                val || ''
            );
        });

        this.filtroNave.valueChanges.subscribe((val) => {
            const filterValue = typeof val === 'string' ? val : (val as any)?.name || '';
            this.filteredVesselsOptions = this._filter(
                this.vesselsOptions,
                filterValue
            );
            if (!filterValue) {
                this.filtroEtdDesde.enable();
            }
        });
    }

    onNaveSelected(event: MatAutocompleteSelectedEvent): void {
        if (event.option.value) {
            // Reset all other filters
            this.filtroPol.setValue('');
            this.filtroPod.setValue('');
            this.filtroCarrier.setValue('');
            this.filtroServicio.setValue(null);
            this.filtroEtdHasta.setValue(null);
            this.filtroEtaDesde.setValue(null);
            this.filtroEtaHasta.setValue(null);
            // ETD Desde = hoy, bloqueado
            this.filtroEtdDesde.setValue(new Date());
            this.filtroEtdDesde.disable();
        } else {
            // "Todas las naves" seleccionado
            this.filtroEtdDesde.enable();
        }
    }

    private _filter(options: any[], value: string): any[] {
        const filterValue = value.toLowerCase();
        return options.filter((option) =>
            option.name.toLowerCase().includes(filterValue)
        );
    }

    private buildFilters(): ItinerarioFiltroDto {
        const filters: ItinerarioFiltroDto = {};

        if (this.filtroPol.value) filters.polId = this.filtroPol.value;
        if (this.filtroPod.value) filters.podId = this.filtroPod.value;
        if (this.filtroCarrier.value)
            filters.carrierId = this.filtroCarrier.value;
        if (this.filtroNave.value)
            filters.vesselId = this.filtroNave.value;
        if (this.filtroServicio.value)
            filters.typeOfServiceId = this.filtroServicio.value;

        if (this.filtroEtdDesde.value)
            filters.dateFrom = this.formatDate(this.filtroEtdDesde.value);
        if (this.filtroEtdHasta.value)
            filters.dateTo = this.formatDate(this.filtroEtdHasta.value);

        if (this.filtroEtaDesde.value)
            filters.dateEtaFrom = this.formatDate(this.filtroEtaDesde.value);
        if (this.filtroEtaHasta.value)
            filters.dateEtaTo = this.formatDate(this.filtroEtaHasta.value);

        return filters;
    }

    private formatDate(date: Date): string {
        return formatDateForApi(date);
    }

    // Check if all required filters are present (POD + at least one date, OR nave selected)
    hasRequiredFilters(): boolean {
        if (this.filtroNave.value) return true;
        const hasPod = !!this.filtroPod.value;
        const hasDate = this.hasAnyDateFilter();
        return hasPod && hasDate;
    }

    // Check if any date filter is set (any of the 4 date fields)
    hasAnyDateFilter(): boolean {
        return !!(
            this.filtroEtdDesde.value ||
            this.filtroEtdHasta.value ||
            this.filtroEtaDesde.value ||
            this.filtroEtaHasta.value
        );
    }

    // Called by Search Button or Filter Enter
    aplicarFiltros(): void {
        // Validate required filters
        if (!this.hasRequiredFilters()) {
            return;
        }

        this.showRequiredFiltersMessage = false;
        this.paginaActual = 1;

        // Save State with Page 1 (Reset)
        this.stateService.updateFilters({
            page: 1,
            pol: this.filtroPol.value || '',
            pod: this.filtroPod.value || '',
            carrier: this.filtroCarrier.value || '',
            vessel: this.filtroNave.value || '',
            typeOfService: this.filtroServicio.value || undefined,
            startDate: this.filtroEtdDesde.value || undefined,
            endDate: this.filtroEtdHasta.value || undefined,
            dateEtaFrom: this.filtroEtaDesde.value || undefined,
            dateEtaTo: this.filtroEtaHasta.value || undefined,
        });

        this.navigationService.applyFilters();
        this.buscarItinerarios();
    }

    buscarItinerarios(): void {
        this.isLoading = true;
        this.hasError = false;

        const filtersBase = this.buildFilters();
        const filtersDto: ItinerarioFiltroDto = {
            ...filtersBase,
            page: this.paginaActual,
            limit: this.elementosPorPagina,
        };

        this.itinerarioService.search(filtersDto).subscribe({
            next: (response) => {
                this.dataSource.data = response.data || [];
                this.totalRegistros = response.total || 0;
                this.isLoading = false;

                // Update pagination from response if needed/adjusted
                this.paginaActual = response.page || this.paginaActual;
            },
            error: (err: any) => {
                console.error('Error loading itinerarios', err);
                this.errorMessage =
                    'No se pudieron cargar los itinerarios. Intente nuevamente.';
                this.hasError = true;
                this.isLoading = false;
            },
        });
    }

    limpiarFiltros(): void {
        this.filtroPol.setValue('');
        this.filtroPod.setValue('');
        this.filtroCarrier.setValue('');
        this.filtroNave.setValue('');
        this.filtroServicio.setValue(null);
        this.filtroEtdDesde.enable();
        this.filtroEtdDesde.setValue(null);
        this.filtroEtdHasta.setValue(null);
        this.filtroEtaDesde.setValue(null);
        this.filtroEtaHasta.setValue(null);

        // Clear State & URL
        this.navigationService.clearFilters();

        this.paginaActual = 1;
        this.showRequiredFiltersMessage = true;
        this.dataSource.data = [];
        this.totalRegistros = 0;
    }

    hasActiveFilters(): boolean {
        return !!(
            this.filtroPol.value ||
            this.filtroPod.value ||
            this.filtroCarrier.value ||
            this.filtroNave.value ||
            this.filtroServicio.value ||
            this.filtroEtdDesde.value ||
            this.filtroEtdHasta.value ||
            this.filtroEtaDesde.value ||
            this.filtroEtaHasta.value
        );
    }

    onPageChange(event: PageEvent): void {
        this.paginaActual = event.pageIndex + 1;
        this.elementosPorPagina = event.pageSize;

        // Update State (Pagination)
        this.stateService.updateFilters({
            page: this.paginaActual,
            limit: this.elementosPorPagina,
        });

        this.navigationService.applyFilters();
        this.buscarItinerarios();
    }

    navigateToDetail(item: {
        voyageId: number;
        polName?: string;
        podName?: string;
    }): void {
        this.navigationService.openDetailInNewTab(item.voyageId, {
            polName: item.polName,
            podName: item.podName,
        });
    }

    openWizardIE(item: ItinerarioItemDto): void {
        if (!item.voyageId) return;
        this.ieNavigationService.openWizardFromItinerary(
            item.voyageId,
            item.portOfLoadingId ?? 0,
            item.portOfDischargeId ?? 0
        );
    }

    // Helper for logos (same as before)
    getCarrierLogo(carrierName: string): string {
        return '';
    }

    hasCarrierLogo(carrierName: string): boolean {
        return false; // Force fallback to initial letter
    }

    formatDisplayDate(date: string | undefined | null): string {
        return date || 'N/A';
    }

    /**
     * Export all filtered data to Excel with professional styling
     */
    async exportarExcel(): Promise<void> {
        if (this.totalRegistros === 0) {
            return;
        }

        this.isLoading = true;

        try {
            // Build filters without pagination to get all data
            const filters = this.buildFilters();

            // Subscribe to get the data
            this.itinerarioService
                .search({ ...filters, page: 1, limit: 10000 })
                .subscribe({
                    next: async (response) => {
                        try {
                            await this.generateStyledExcel(response.data);
                            this.isLoading = false;
                        } catch (error) {
                            console.error('Error generando Excel:', error);
                            this.isLoading = false;
                        }
                    },
                    error: (err) => {
                        console.error('Error obteniendo datos:', err);
                        this.isLoading = false;
                    },
                });
        } catch (error) {
            console.error('Error exportando Excel:', error);
            this.isLoading = false;
        }
    }

    /**
     * Generate Excel file with professional styling matching Liquidaciones Agencia format
     */
    private async generateStyledExcel(
        data: ItinerarioItemDto[]
    ): Promise<void> {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Itinerarios');

        // OPB Brand Colors
        const colors = {
            primary: '1e3a8a', // Azul primario
            secondary: '3b82f6', // Azul secundario
            dark: '1e293b', // Azul oscuro
            gray: '64748b', // Gris
            light: 'f1f5f9', // Gris claro
            white: 'ffffff', // Blanco
        };

        // Try to load logo
        let logoArrayBuffer: ArrayBuffer | null = null;
        try {
            const baseHref =
                document.querySelector('base')?.getAttribute('href') || '/';
            const logoPath = `${baseHref}assets/images/logos/logotipo-fondo-claro.png`;
            const logoResponse = await fetch(logoPath);
            if (logoResponse.ok) {
                logoArrayBuffer = await logoResponse.arrayBuffer();
            }
        } catch (error) {
            console.warn('No se pudo cargar el logo');
        }

        // Add logo if available
        if (logoArrayBuffer) {
            try {
                const logoId = workbook.addImage({
                    buffer: logoArrayBuffer,
                    extension: 'png',
                });
                worksheet.addImage(logoId, {
                    tl: { col: 0.2, row: 0.2 },
                    ext: { width: 150, height: 50 },
                });
            } catch (error) {
                console.warn('Error al agregar logo');
            }
        }

        // Title row
        const titleRow = worksheet.getRow(1);
        titleRow.height = 40;
        const titleCell = worksheet.getCell('D1');
        titleCell.value = 'ITINERARIOS DE NAVES';
        titleCell.font = {
            name: 'Calibri',
            size: 20,
            bold: true,
            color: { argb: 'FF' + colors.primary },
        };
        titleCell.alignment = { vertical: 'middle', horizontal: 'left' };
        worksheet.mergeCells('D1:G1');

        // Date row
        const dateRow = worksheet.getRow(2);
        dateRow.height = 25;
        const dateCell = worksheet.getCell('D2');
        dateCell.value = `Generado el: ${new Date().toLocaleDateString(
            'es-CL',
            {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
            }
        )}`;
        dateCell.font = {
            name: 'Calibri',
            size: 11,
            color: { argb: 'FF' + colors.gray },
        };
        dateCell.alignment = { vertical: 'middle', horizontal: 'left' };
        worksheet.mergeCells('D2:G2');

        // Empty row
        worksheet.getRow(3).height = 10;

        // Column definitions
        const columnDefs = [
            { header: 'Nave', key: 'vesselName', width: 22 },
            { header: 'Viaje', key: 'voyageNumber', width: 12 },
            { header: 'Naviera', key: 'carrierName', width: 18 },
            { header: 'Puerto Embarque (POL)', key: 'polName', width: 25 },
            { header: 'Puerto Descarga (POD)', key: 'podName', width: 25 },
            { header: 'ETD', key: 'etd', width: 12 },
            { header: 'ETA', key: 'eta', width: 12 },
            { header: 'Tiempo Tránsito (días)', key: 'transitTime', width: 18 },
        ];

        // Set column widths
        columnDefs.forEach((col, index) => {
            worksheet.getColumn(index + 1).width = col.width;
        });

        // Header row (row 4)
        const headerRow = worksheet.getRow(4);
        headerRow.height = 30;
        columnDefs.forEach((col, index) => {
            const cell = worksheet.getCell(4, index + 1);
            cell.value = col.header;
            cell.font = {
                name: 'Calibri',
                size: 12,
                bold: true,
                color: { argb: 'FFFFFFFF' },
            };
            cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'FF' + colors.primary },
            };
            cell.alignment = {
                vertical: 'middle',
                horizontal: 'center',
                wrapText: true,
            };
            cell.border = {
                top: { style: 'medium', color: { argb: 'FF' + colors.dark } },
                bottom: {
                    style: 'medium',
                    color: { argb: 'FF' + colors.dark },
                },
                left: { style: 'thin', color: { argb: 'FFFFFFFF' } },
                right: { style: 'thin', color: { argb: 'FFFFFFFF' } },
            };
        });

        // Data rows
        data.forEach((item, index) => {
            const rowNumber = 5 + index;
            const row = worksheet.getRow(rowNumber);
            row.height = 22;

            const rowData = [
                item.vesselName || '-',
                item.voyageNumber || '-',
                item.carrierName || '-',
                item.polName || '-',
                item.podName || 'Por Confirmar',
                item.etd || '-',
                item.eta || '-',
                item.transitTime || 0,
            ];

            rowData.forEach((value, colIndex) => {
                const cell = row.getCell(colIndex + 1);
                cell.value = value;

                // Alternating row colors
                const fillColor =
                    index % 2 === 0
                        ? { argb: 'FFFFFFFF' }
                        : { argb: 'FF' + colors.light };

                cell.fill = {
                    type: 'pattern',
                    pattern: 'solid',
                    fgColor: fillColor,
                };

                cell.font = {
                    name: 'Calibri',
                    size: 11,
                    color: { argb: 'FF000000' },
                };

                // Alignment based on type
                if (typeof value === 'number') {
                    cell.alignment = {
                        vertical: 'middle',
                        horizontal: 'center',
                    };
                    cell.numFmt = '#,##0';
                } else {
                    cell.alignment = { vertical: 'middle', horizontal: 'left' };
                }

                // Borders
                cell.border = {
                    top: { style: 'thin', color: { argb: 'FFd1d5db' } },
                    bottom: { style: 'thin', color: { argb: 'FFd1d5db' } },
                    left: { style: 'thin', color: { argb: 'FFd1d5db' } },
                    right: { style: 'thin', color: { argb: 'FFd1d5db' } },
                };
            });
        });

        // Generate and download file
        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const fecha = new Date().toISOString().split('T')[0];
        a.download = `itinerarios_${fecha}.xlsx`;
        a.click();
        window.URL.revokeObjectURL(url);
    }

    // ============== Route Visualization Helpers ==============

    /**
     * Get route segments: before POL, POL-to-POD (active), after POD
     */
    getRouteSegments(route: any[] | undefined): {
        beforePol: any[];
        active: any[];
        afterPod: any[];
    } {
        if (!route || route.length === 0) {
            return { beforePol: [], active: [], afterPod: [] };
        }

        const polIndex = route.findIndex((r) => r.isPol);
        const podIndex = route.findIndex((r) => r.isPod);

        // If POL/POD not found, treat entire route as active
        if (polIndex === -1 || podIndex === -1) {
            return { beforePol: [], active: route, afterPod: [] };
        }

        return {
            beforePol: route.slice(0, polIndex),
            active: route.slice(polIndex, podIndex + 1),
            afterPod: route.slice(podIndex + 1),
        };
    }

    /**
     * Get intermediate stops (between POL and POD, excluding POL/POD themselves)
     */
    getIntermediateStops(route: any[] | undefined): any[] {
        const segments = this.getRouteSegments(route);
        // Active segment includes POL and POD, so remove first and last
        if (segments.active.length <= 2) return [];
        return segments.active.slice(1, -1);
    }

    /**
     * Get visible intermediate stops (max 5, for display)
     */
    getVisibleIntermediateStops(route: any[] | undefined): any[] {
        const intermediate = this.getIntermediateStops(route);
        const MAX_VISIBLE = 12;
        return intermediate.slice(0, MAX_VISIBLE);
    }

    /**
     * Get count of hidden stops (for "+N más" badge)
     */
    getHiddenStopsCount(route: any[] | undefined): number {
        const intermediate = this.getIntermediateStops(route);
        const MAX_VISIBLE = 12;
        return Math.max(0, intermediate.length - MAX_VISIBLE);
    }

    /**
     * Get hidden stops (for tooltip)
     */
    getHiddenStops(route: any[] | undefined): any[] {
        const intermediate = this.getIntermediateStops(route);
        const MAX_VISIBLE = 12;
        return intermediate.slice(MAX_VISIBLE);
    }

    /**
     * Helper to format a list of stops into 'PORT - DATE' (new line separated)
     */
    private getStopsTooltip(stops: any[]): string {
        if (!stops || stops.length === 0) return '';
        const stopLines = stops.map((s) => {
            const date = s.eta || s.etd || '-';
            return `${s.portName} - ${date}`;
        });
        return stopLines.join('\n');
    }

    /**
     * Get formatted tooltip text for hidden stops
     */
    getHiddenStopsTooltip(route: any[] | undefined): string {
        const hiddenStops = this.getHiddenStops(route);
        return this.getStopsTooltip(hiddenStops);
    }

    /**
     * Get formatted tooltip for Before POL stops
     */
    getBeforePolTooltip(route: any[] | undefined): string {
        const stops = this.getRouteSegments(route).beforePol;
        return this.getStopsTooltip(stops);
    }

    /**
     * Get formatted tooltip for After POD stops
     */
    getAfterPodTooltip(route: any[] | undefined): string {
        const stops = this.getRouteSegments(route).afterPod;
        return this.getStopsTooltip(stops);
    }

    /**
     * Get total intermediate stops count
     */
    getIntermediateCount(route: any[] | undefined): number {
        return this.getIntermediateStops(route).length;
    }

    /**
     * Get POL stop from route
     */
    getPolStop(route: any[] | undefined): any | null {
        return route?.find((r) => r.isPol) || null;
    }

    /**
     * Get POD stop from route
     */
    getPodStop(route: any[] | undefined): any | null {
        return route?.find((r) => r.isPod) || null;
    }
}
